import {
  isZhLocale,
  localeToContentLang,
  normalizeSiteLanguage,
  pickLocalized,
  type SiteLanguageCode,
} from '@yoyoclub/shared';

/**
 * Display-time Simplified/Traditional conversion of host-written Chinese
 * (docs/prd-bilingual-content.md §5.1). Stored text never changes.
 *
 * OpenCC's standard conversion (phrase-aware for one-to-many characters, no
 * regional vocabulary), version pinned in package.json. The dictionary for a
 * script loads only when that script is the current language: Traditional
 * readers fetch the Simplified-to-Traditional table, Simplified readers the
 * reverse (for hosts who write Traditional), English readers nothing. Until it
 * arrives, text shows as written.
 */
type ChineseCode = Exclude<SiteLanguageCode, 'en'>;
type Convert = (text: string) => string;

const converters: Partial<Record<ChineseCode, Convert>> = {};
const pending: Partial<Record<ChineseCode, Promise<void>>> = {};
const listeners = new Set<() => void>();
// Bumped whenever a converter loads, so subscribers can tell something changed.
let scriptVersion = 0;
const cache = new Map<string, string>();
const CACHE_LIMIT = 2000;
const HAS_CJK = /[\u3400-\u4dbf\u4e00-\u9fff\uf900-\ufaff]/;

async function buildConverter(target: ChineseCode): Promise<Convert> {
  if (target === 'zh-TW') {
    const { Converter } = await import('opencc-js/cn2t');
    return Converter({ from: 'cn', to: 'tw' });
  }
  const { Converter } = await import('opencc-js/t2cn');
  return Converter({ from: 'tw', to: 'cn' });
}

/** Load the converter into `lang`'s script. A no-op for English or once loaded. */
export function loadScript(lang: string | null | undefined): Promise<void> {
  const target = normalizeSiteLanguage(lang);
  if (target === 'en' || converters[target]) return Promise.resolve();
  pending[target] ??= buildConverter(target)
    .then((convert) => {
      converters[target] = convert;
      scriptVersion += 1;
      listeners.forEach((notify) => notify());
    })
    .catch(() => {
      // Text keeps showing as written; the next language change retries.
      delete pending[target];
    });
  return pending[target];
}

export function getScriptVersion(): number {
  return scriptVersion;
}

/** Called whenever a converter becomes available, so the UI can re-render. */
export function onScriptLoaded(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Host-written Chinese in the script of `lang`. English and anything without
 * Chinese characters pass through unchanged (PRD §5.1: under English, Chinese
 * shows as stored).
 */
export function toScript(text: string, lang: string | null | undefined): string {
  if (!text || !isZhLocale(lang) || !HAS_CJK.test(text)) return text;
  const target = normalizeSiteLanguage(lang) as ChineseCode;
  const convert = converters[target];
  if (!convert) return text;
  const key = `${target}\u0000${text}`;
  let out = cache.get(key);
  if (out === undefined) {
    out = convert(text);
    if (cache.size >= CACHE_LIMIT) cache.clear();
    cache.set(key, out);
  }
  return out;
}

/** Between items of a short list: 、 in Chinese, a comma in English. */
export function listSeparator(lang: string | null | undefined): string {
  return localeToContentLang(lang) === 'ZH' ? '、' : ', ';
}

/**
 * The one display rule for host content (PRD §5.1): the current language's
 * side, else the other side, with Chinese shown in the reader's script.
 */
export function localize(
  zh: string | null | undefined,
  en: string | null | undefined,
  lang: string | null | undefined
): string {
  return toScript(pickLocalized(zh, en, lang), lang);
}

/**
 * A backend list title in two lines (PRD §5.2): the user's language on top,
 * the other language below. The top line falls back to the other side, and
 * then there is no second line. Chinese follows the user's script.
 */
export function bilingualLines(
  zh: string | null | undefined,
  en: string | null | undefined,
  lang: string | null | undefined
): [string, string] {
  const z = toScript(zh?.trim() ?? '', lang);
  const e = en?.trim() ?? '';
  const [first, second] = localeToContentLang(lang) === 'ZH' ? [z, e] : [e, z];
  if (!first) return [second, ''];
  // Identical sides (or a field the API pre-filled with the fallback) show once.
  return [first, second === first ? '' : second];
}

/**
 * Two-line title for a compound label such as "Activity - Pass": every part
 * ordered like bilingualLines, joined per line. `fallback` covers rows from
 * an API that sent no raw sides.
 */
export function bilingualLinesJoined(
  pairs: Array<[string | null | undefined, string | null | undefined]>,
  lang: string | null | undefined,
  fallback = ''
): [string, string] {
  const lines = pairs.map(([zh, en]) => bilingualLines(zh, en, lang)).filter(([first]) => first);
  const top = lines.map(([first]) => first).join(' - ');
  if (!top) return [fallback, ''];
  const bottom = lines.some(([, second]) => second)
    ? lines.map(([first, second]) => second || first).join(' - ')
    : '';
  return [top, bottom === top ? '' : bottom];
}

/**
 * Canonical form for script-insensitive search (PRD §5.5), the same rule as
 * the backend: Unicode-normalized, Simplified, lower case. Call
 * loadSearchNormalizer first; until it loads, only the script folding is skipped.
 */
export function normalizeForSearch(text: string): string {
  const toSimplified = converters['zh-CN'];
  const normalized = text.normalize('NFKC');
  return (toSimplified ? toSimplified(normalized) : normalized).toLowerCase();
}

/** Loads the Traditional-to-Simplified table normalizeForSearch uses. */
export function loadSearchNormalizer(): Promise<void> {
  return loadScript('zh-CN');
}

/** Test hook: drop loaded converters and cached results. */
export function resetScriptForTests(): void {
  for (const key of Object.keys(converters) as ChineseCode[]) delete converters[key];
  for (const key of Object.keys(pending) as ChineseCode[]) delete pending[key];
  cache.clear();
}
